// Piccoli componenti UI riutilizzabili (DESIGN_TOKENS.md §4/§5).
import type { ReactNode } from 'react';
import { ChevronRight, Copy } from 'lucide-react';
import { useCopy } from './Toast';

export function IconChip({ children, solid, sm }: { children: ReactNode; solid?: boolean; sm?: boolean }) {
  return (
    <div className={`iconchip${sm ? ' iconchip--sm' : ''}${solid ? ' iconchip--solid' : ''}`}>{children}</div>
  );
}

export function ProgressBar({ value, near }: { value: number; near?: boolean }) {
  return (
    <div className="progress" role="progressbar" aria-valuenow={Math.round(value * 100)} aria-valuemin={0} aria-valuemax={100}>
      <div className={`progress__fill${near ? ' is-near' : ''}`} style={{ width: `${Math.round(value * 100)}%` }} />
    </div>
  );
}

export function ListRow({
  chip,
  title,
  meta,
  trailing,
  onClick,
}: {
  chip: ReactNode;
  title: string;
  meta?: ReactNode;
  trailing?: ReactNode;
  onClick?: () => void;
}) {
  return (
    <button type="button" className="row" onClick={onClick}>
      {chip}
      <div className="row__body">
        <div className="row__title">{title}</div>
        {meta && <div className="row__meta">{meta}</div>}
      </div>
      {trailing ?? <ChevronRight size={18} className="row__chevron" />}
    </button>
  );
}

export function KeyValueRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  const copy = useCopy();
  return (
    <div className="kv">
      <div>
        <div className="kv__label">{label}</div>
        <div className={`kv__value${mono ? ' kv__value--mono' : ''}`}>{value}</div>
      </div>
      <button type="button" className="kv__copy" aria-label={`Copia ${label}`} onClick={() => copy(value)}>
        <Copy size={16} />
      </button>
    </div>
  );
}

export function Eyebrow({ children }: { children: ReactNode }) {
  return <div className="eyebrow">{children}</div>;
}

export function EmptyState({
  message,
  title,
  icon,
  cta,
}: {
  message: string;
  title?: string;
  icon?: ReactNode;
  cta?: ReactNode;
}) {
  return (
    <div className="empty">
      {icon && (
        <div className="empty__illu">
          {icon}
          <span className="empty__badge">+</span>
        </div>
      )}
      {title && <div className="empty__title">{title}</div>}
      <p>{message}</p>
      {cta && <div className="empty__cta">{cta}</div>}
    </div>
  );
}

export function SkeletonList({ rows = 3 }: { rows?: number }) {
  return (
    <div className="list">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="skeleton" style={{ height: 72 }} />
      ))}
    </div>
  );
}

export function Chips<T extends string>({
  options,
  active,
  onChange,
}: {
  options: { value: T; label: string }[];
  active: T;
  onChange: (v: T) => void;
}) {
  return (
    <div className="chips">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          className={`chip${o.value === active ? ' is-active' : ''}`}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}
